import { useState, useCallback, useLayoutEffect, useEffect, useRef } from 'react';

/**
 * Positions a sliding pill behind the active practice-dashboard tab button.
 * Visual-only — does not affect tab selection or routing.
 */
export function usePracticeDashboardTabIndicator(activeTab, tabCount = 0) {
  const tabsWrapRef = useRef(null);
  const [tabIndicatorStyle, setTabIndicatorStyle] = useState({ opacity: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  const updateTabIndicator = useCallback(() => {
    const wrap = tabsWrapRef.current;
    if (!wrap) return;

    const activeButton = wrap.querySelector('.tab-item.active .tab-button');
    if (!activeButton) {
      setTabIndicatorStyle({ opacity: 0 });
      return;
    }

    const wrapRect = wrap.getBoundingClientRect();
    const btnRect = activeButton.getBoundingClientRect();

    setTabIndicatorStyle({
      width: `${btnRect.width}px`,
      height: `${btnRect.height}px`,
      transform: `translate(${btnRect.left - wrapRect.left}px, ${btnRect.top - wrapRect.top}px)`,
      opacity: 1,
    });
    setIndicatorReady(true);
  }, []);

  useLayoutEffect(() => {
    const wrap = tabsWrapRef.current;
    const activeButton = wrap?.querySelector('.tab-item.active .tab-button');
    activeButton?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    updateTabIndicator();
  }, [activeTab, tabCount, updateTabIndicator]);

  useEffect(() => {
    const wrap = tabsWrapRef.current;
    if (!wrap) return undefined;

    const tabsList = wrap.querySelector('.practice-dashboard-tabs');
    const resizeObserver = new ResizeObserver(updateTabIndicator);

    resizeObserver.observe(wrap);
    if (tabsList) {
      resizeObserver.observe(tabsList);
      tabsList.addEventListener('scroll', updateTabIndicator, { passive: true });
    }
    window.addEventListener('resize', updateTabIndicator);

    return () => {
      resizeObserver.disconnect();
      if (tabsList) {
        tabsList.removeEventListener('scroll', updateTabIndicator);
      }
      window.removeEventListener('resize', updateTabIndicator);
    };
  }, [updateTabIndicator]);

  return {
    tabsWrapRef,
    tabIndicatorStyle,
    indicatorReady,
  };
}
